import axios from "axios";
import { getResolvedAdminApiKey } from "./utils/adminApiKey";
import { buildDedupKey, dedupeRequest } from "./services/requestDedup";
import { notifyIfInvalidSession } from "./services/sessionInvalidation";
import {
  generateId,
  getGlobalPerfState,
  isPerfEnabled,
  onApiEnd,
  onApiStart,
} from "./utils/perf/perfMonitor";

// Cross-site API calls (e.g. app + API on different subdomains) require credentials on every axios use.
axios.defaults.withCredentials = true;

function readHeader(config, name) {
  const h = config.headers;
  if (!h) return null;
  if (typeof h.get === "function") {
    return h.get(name) ?? h.get(name.toLowerCase());
  }
  return h[name] ?? h[name.toLowerCase()];
}

function shouldBypassAxiosDedup(config) {
  const skip = readHeader(config, "X-HR-Skip-Dedup");
  if (skip === "1" || skip === "true") return true;
  if (config.signal) return true;
  const rt = config.responseType;
  if (rt === "blob" || rt === "arraybuffer" || rt === "document") return true;
  const d = config.data;
  if (typeof FormData !== "undefined" && d instanceof FormData) return true;
  if (typeof Blob !== "undefined" && d instanceof Blob) return true;
  return false;
}

function axiosDedupKey(config) {
  const method = String(config.method || "get").toUpperCase();
  let url;
  try {
    url = axios.getUri(config);
  } catch {
    url = `${config.baseURL || ""}${config.url || ""}`;
  }
  const d = config.data;
  let bodyPart = d;
  if (typeof FormData !== "undefined" && d instanceof FormData) bodyPart = "__FormData__";
  else if (typeof Blob !== "undefined" && d instanceof Blob) bodyPart = "__Blob__";
  return buildDedupKey(method, url, {}, bodyPart ?? null);
}

const baseAdapter = Array.isArray(axios.defaults.adapter)
  ? axios.defaults.adapter[0]
  : axios.defaults.adapter;

if (typeof baseAdapter === "function") {
  axios.defaults.adapter = function hrInFlightDedupAdapter(config) {
    if (shouldBypassAxiosDedup(config)) {
      return baseAdapter(config);
    }
    const key = axiosDedupKey(config);
    return dedupeRequest(key, () => baseAdapter(config));
  };
}

const ADMIN_API_PATH = "/api/admin/";

function requestUrlLooksLikeAdminApi(config) {
  const rel = config.url || "";
  if (rel.includes(ADMIN_API_PATH)) return true;
  const base = config.baseURL || "";
  const combined = rel.startsWith("http") ? rel : `${base}${rel}`.replace(/([^:])\/{2,}/g, "$1/");
  return combined.includes(ADMIN_API_PATH);
}

function setAdminApiHeader(config, key) {
  if (!key) return;
  const h = config.headers;
  if (h && typeof h.set === "function") {
    h.set("X-Admin-Api-Key", key);
  } else {
    config.headers = { ...config.headers, "X-Admin-Api-Key": key };
  }
}

let missingAdminKeyWarned = false;

function axiosConfigUrl(config) {
  if (!config) return "";
  try {
    return axios.getUri(config);
  } catch {
    return `${config.baseURL || ""}${config.url || ""}`;
  }
}

function inspectAxiosResponseForInvalidSession(response) {
  void notifyIfInvalidSession({
    status: response?.status,
    data: response?.data,
    url: axiosConfigUrl(response?.config),
  });
}

function inspectAxiosErrorForInvalidSession(error) {
  void notifyIfInvalidSession({
    status: error?.response?.status,
    data: error?.response?.data,
    url: axiosConfigUrl(error?.config || error?.response?.config),
  });
}

axios.interceptors.request.use((config) => {
  if (isPerfEnabled()) {
    const perfState = getGlobalPerfState();
    const perfRequestId = generateId("req");
    config.__hrPerf = {
      requestId: perfRequestId,
      startedAtMs:
        typeof performance !== "undefined" && typeof performance.now === "function"
          ? performance.now()
          : Date.now(),
    };
    config.headers = {
      ...(config.headers || {}),
      "X-Perf-Session-Id": perfState.sessionId,
      "X-Perf-Request-Id": perfRequestId,
    };
    onApiStart({
      method: config.method,
      url: config.url,
      requestId: perfRequestId,
    });
  }

  if (!requestUrlLooksLikeAdminApi(config)) {
    return config;
  }
  const key = getResolvedAdminApiKey();
  if (key) {
    setAdminApiHeader(config, key);
    return config;
  }
  if (!missingAdminKeyWarned) {
    missingAdminKeyWarned = true;
    if (isPerfEnabled()) {
      console.warn("Missing VITE_ADMIN_API_KEY for admin API request");
    }
  }
  return config;
});

axios.interceptors.response.use(
  (response) => {
    inspectAxiosResponseForInvalidSession(response);
    if (!isPerfEnabled()) return response;
    const startedAtMs = response?.config?.__hrPerf?.startedAtMs;
    const requestId = response?.config?.__hrPerf?.requestId;
    const nowMs =
      typeof performance !== "undefined" && typeof performance.now === "function"
        ? performance.now()
        : Date.now();
    onApiEnd({
      method: response?.config?.method,
      url: response?.config?.url,
      requestId,
      status: response?.status,
      durationMs: startedAtMs != null ? nowMs - startedAtMs : 0,
      data: response?.data,
    });
    return response;
  },
  (error) => {
    inspectAxiosErrorForInvalidSession(error);
    if (!isPerfEnabled()) return Promise.reject(error);
    const cfg = error?.config || {};
    const startedAtMs = cfg?.__hrPerf?.startedAtMs;
    const requestId = cfg?.__hrPerf?.requestId;
    const nowMs =
      typeof performance !== "undefined" && typeof performance.now === "function"
        ? performance.now()
        : Date.now();
    onApiEnd({
      method: cfg?.method,
      url: cfg?.url,
      requestId,
      status: error?.response?.status ?? 0,
      durationMs: startedAtMs != null ? nowMs - startedAtMs : 0,
      data: error?.response?.data,
    });
    return Promise.reject(error);
  }
);
